import useDateHelperUtils from './dateHelperUtils.ts';
import useUtils from './utils.ts';
import { IRoom, RoomTypeAvailability } from 'src/types';
import {
  RateDTO,
  BookingEngineArticleDTO,
} from 'src/services/api/data-contracts';
import { IReservationItemService } from 'src/types/common.ts';

const dateUtils = useDateHelperUtils();
const utils = useUtils();

function calculatePerNightRate(rate: number, from: string, to: string) {
  return rate / dateUtils.calculateNumberOfNights(from, to);
}

function calculateLowestRoomRate(
  room: IRoom,
  rates: RateDTO[],
  useOriginalValues: boolean,
): number {
  let total = 0;
  const roomRates = [];

  rates.forEach((rate: RateDTO) => {
    let nightlyRate = 0;
    if (useOriginalValues) {
      nightlyRate = calculateRoomRate(
        room.numberOfAdults,
        room.numberOfChildren,
        room.numberOfInfants,
        rate.OriginalBaseRate,
        rate.BaseNumberOfAdults,
        rate.OriginalCostPerRoom,
        rate.OriginalCostPerAdult,
        rate.OriginalCostPerChild,
        rate.OriginalCostPerInfant,
        rate.OriginalPerPersonCostPerAdult,
        rate.OriginalPerPersonCostPerChild,
        rate.OriginalPerPersonCostPerInfant,
      );
    } else {
      nightlyRate = calculateRoomRate(
        room.numberOfAdults,
        room.numberOfChildren,
        room.numberOfInfants,
        rate.BaseRate,
        rate.BaseNumberOfAdults,
        rate.CostPerRoom,
        rate.CostPerAdult,
        rate.CostPerChild,
        rate.CostPerInfant,
        rate.PerPersonCostPerAdult,
        rate.PerPersonCostPerChild,
        rate.PerPersonCostPerInfant,
      );
    }
    roomRates.push(nightlyRate);
  });

  const minimumRate = Math.min(...roomRates);

  total = total + minimumRate;

  return total;
}

function calculateTotalRoomRate(
  rooms: Array<IRoom>,
  roomTypes: Array<RoomTypeAvailability>,
  useOriginalValues: boolean,
): number {
  let total = 0;

  rooms.forEach((v: IRoom) => {
    const roomRates = [];

    roomTypes.forEach((rt: RoomTypeAvailability) => {
      const isRoomTypeOccupancyValid = utils.checkIfOccupancyIsValid(
        v.numberOfAdults,
        v.numberOfChildren,
        v.numberOfInfants,
        rt.RoomType.MaximumOccupancy,
        rt.RoomType.MinimumOccupancy,
        rt.RoomType.AdultMaximumOccupancy,
        rt.RoomType.ChildMaximumOccupancy,
      );
      if (isRoomTypeOccupancyValid) {
        rt.Rates.forEach((rate: RateDTO) => {
          let ratePlanOccupancyIsValid = true;
          if (rate.UseOccupancyForRatePlan) {
            ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
              v.numberOfAdults,
              v.numberOfChildren,
              v.numberOfInfants,
              utils.getSmallestMaximumValue(
                rate.MaximumOccupancy,
                rt.RoomType.MaximumOccupancy,
              ),
              utils.getLargestMinimumValue(
                rate.MinimumOccupancy,
                rt.RoomType.MinimumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                rate.AdultMaximumOccupancy,
                rt.RoomType.AdultMaximumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                rate.ChildMaximumOccupancy,
                rt.RoomType.ChildMaximumOccupancy,
              ),
            );
          }
          if (ratePlanOccupancyIsValid) {
            let nightlyRate = 0;
            if (useOriginalValues) {
              nightlyRate = calculateRoomRate(
                v.numberOfAdults,
                v.numberOfChildren,
                v.numberOfInfants,
                rate.OriginalBaseRate,
                rate.BaseNumberOfAdults,
                rate.OriginalCostPerRoom,
                rate.OriginalCostPerAdult,
                rate.OriginalCostPerChild,
                rate.OriginalCostPerInfant,
                rate.OriginalPerPersonCostPerAdult,
                rate.OriginalPerPersonCostPerChild,
                rate.OriginalPerPersonCostPerInfant,
              );
            } else {
              nightlyRate = calculateRoomRate(
                v.numberOfAdults,
                v.numberOfChildren,
                v.numberOfInfants,
                rate.BaseRate,
                rate.BaseNumberOfAdults,
                rate.CostPerRoom,
                rate.CostPerAdult,
                rate.CostPerChild,
                rate.CostPerInfant,
                rate.PerPersonCostPerAdult,
                rate.PerPersonCostPerChild,
                rate.PerPersonCostPerInfant,
              );
            }
            roomRates.push(nightlyRate);
          }
        });
      }
    });

    const minimumRate = Math.min(...roomRates);

    total = total + minimumRate;
  });

  return total;
}

/*
This method is taken from the backend - see CalculateReservationItemRoomRate from RateHelper.cs
*/
function calculateRoomRate(
  numberOfAdults: number,
  numberOfChildren: number,
  numberOfInfants: number,
  baseRate: number,
  baseNumberOfAdults: number | null,
  amountPerRoom: number,
  additionalAdultRate: number,
  additionalChildRate: number,
  additionalInfantRate: number,
  perPersonAdultRate: number = 0,
  perPersonChildRate: number = 0,
  perPersonInfantRate: number = 0,
): number {
  let reservationItemRoomRate = 0;

  const defaultNumberOfAdults = baseNumberOfAdults ?? 1;

  const additionalAdults =
    numberOfAdults > defaultNumberOfAdults
      ? numberOfAdults - defaultNumberOfAdults
      : 0;
  const childMultiplier =
    numberOfAdults == 0 && numberOfChildren > 0
      ? numberOfChildren - 1
      : numberOfChildren;

  reservationItemRoomRate += baseRate + amountPerRoom;

  reservationItemRoomRate +=
    additionalAdultRate * additionalAdults + // Yes this is correct, only the Base number of adults are "free"
    additionalChildRate * childMultiplier +
    additionalInfantRate * numberOfInfants;

  reservationItemRoomRate +=
    perPersonAdultRate * numberOfAdults + // Yes this is correct, all adults are counted for the per person rate.
    perPersonChildRate * numberOfChildren +
    perPersonInfantRate * numberOfInfants;

  return reservationItemRoomRate;
}

function calcuateTotalServiceCost(
  services: Array<IReservationItemService>,
  totalDays: number,
  totalGuests: number,
): number {
  let cost = 0;
  services.forEach((resItemService: IReservationItemService) => {
    const guestFactor = resItemService.Service.ChargePerPerson
      ? totalGuests
      : 1;

    let serviceQuantity = 1;
    const ct = resItemService.Service.ChargeType;
    switch (ct) {
      case 'PerStay':
        serviceQuantity = 1;
        break;
      case 'PerDay':
        serviceQuantity = totalDays;
        break;
      case 'PerQuantity':
        serviceQuantity = resItemService.Quantity;
        break;
      default:
        break;
    }

    resItemService.Service.Articles.forEach(
      (article: BookingEngineArticleDTO) => {
        cost = cost + article.Price * serviceQuantity * guestFactor;
      },
    );
  });
  return cost;
}

export default function useRateHelperUtils() {
  return {
    calculateRoomRate: calculateRoomRate,
    calcuateTotalServiceCost: calcuateTotalServiceCost,
    calculateTotalRoomRate: calculateTotalRoomRate,
    calculatePerNightRate: calculatePerNightRate,
    calculateLowestRoomRate: calculateLowestRoomRate,
  };
}
